import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import {
  useActivateBotScenario,
  useBotScenarios,
  useCloneBotScenario,
  useCreateBotScenario,
  useDeactivateBotScenario,
  useDeleteBotScenario,
  type BotScenarioRow,
} from '../api/queries.js';
import { useAuthStore } from '../auth/auth-store.js';
import {
  Button,
  ConfirmModal,
  DrawerPanel,
  EmptyState,
  IconButton,
  PageHeader,
  Toggle,
} from '../components/ui/index.js';

export function BotsPage(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const slug = user?.workspaceSlug;
  const navigate = useNavigate();
  const botsQ = useBotScenarios(slug);
  const list = botsQ.data ?? [];

  const createBot = useCreateBotScenario(slug);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  async function handleCreate(): Promise<void> {
    if (!newName.trim()) return;
    const created = await createBot.mutateAsync({
      name: newName.trim(),
      description: newDesc.trim() || undefined,
    });
    setShowCreate(false);
    setNewName('');
    setNewDesc('');
    void navigate({ to: `/bots/${created.id}` });
  }

  return (
    <div className="inbx-page">
      <PageHeader
        title="Бот-сценарии"
        badge={{ count: list.length }}
        actions={
          <Button variant="primary" icon="ti-plus" onClick={() => setShowCreate(true)}>
            Создать сценарий
          </Button>
        }
      />
      <div className="inbx-page__body">
        {!botsQ.isLoading && list.length === 0 ? (
          <EmptyState
            icon="ti-robot"
            title="Сценариев нет"
            description="Создай первый — приветствие, FAQ-бот или квалификация лида"
          />
        ) : (
          <div className="inbx-grid-3">
            {list.map((b) => (
              <BotCard key={b.id} bot={b} workspaceSlug={slug} />
            ))}
          </div>
        )}
      </div>

      <DrawerPanel
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Новый сценарий"
        footer={
          <Button
            variant="primary"
            onClick={handleCreate}
            disabled={createBot.isPending || !newName.trim()}
          >
            {createBot.isPending ? 'Создание…' : 'Создать'}
          </Button>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label className="inbx-label">Название *</label>
            <input
              className="inbx-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Приветственный бот"
              autoFocus
            />
          </div>
          <div>
            <label className="inbx-label">Описание</label>
            <textarea
              className="inbx-input"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder="Краткое описание сценария"
              rows={3}
            />
          </div>
        </div>
      </DrawerPanel>
    </div>
  );
}

function BotCard({
  bot,
  workspaceSlug,
}: {
  bot: BotScenarioRow;
  workspaceSlug: string | undefined;
}): JSX.Element {
  const navigate = useNavigate();
  const activate = useActivateBotScenario(workspaceSlug);
  const deactivate = useDeactivateBotScenario(workspaceSlug);
  const clone = useCloneBotScenario(workspaceSlug);
  const deleteMut = useDeleteBotScenario(workspaceSlug);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleToggle(): void {
    if (bot.isActive) {
      void deactivate.mutate(bot.id);
    } else {
      void activate.mutate(bot.id);
    }
  }

  return (
    <div className="inbx-card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        style={{
          height: 100,
          background: 'linear-gradient(135deg, #EEEDFE, #E1F5EE)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <i className="ti-robot" style={{ fontSize: 38, color: 'var(--inbx-accent)' }} />
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 500, flex: 1 }}>{bot.name}</span>
          <Toggle checked={bot.isActive} onChange={() => handleToggle()} />
        </div>
        <div
          style={{
            fontSize: 'var(--inbx-fz-sm)',
            color: 'var(--inbx-text-tertiary)',
            marginTop: 4,
            minHeight: 32,
          }}
        >
          {bot.description ?? '—'}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <Button
            size="sm"
            variant="primary"
            icon="ti-edit"
            onClick={() => void navigate({ to: `/bots/${bot.id}` })}
          >
            Редактировать
          </Button>
          <IconButton
            icon="ti-copy"
            tooltip="Дублировать"
            onClick={() => void clone.mutate(bot.id)}
            disabled={clone.isPending}
          />
          <IconButton icon="ti-trash" tooltip="Удалить" onClick={() => setConfirmDelete(true)} />
        </div>
      </div>

      <ConfirmModal
        open={confirmDelete}
        title="Удалить сценарий?"
        description={`Сценарий «${bot.name}» будет удалён без возможности восстановления.`}
        confirmLabel="Удалить"
        variant="danger"
        onConfirm={() => {
          deleteMut.mutate(bot.id);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
